import { motion } from "framer-motion";
import { Zap, MessageSquare, Heart, Users } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } }
};

const ImpactItem = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded bg-secondary flex items-center justify-center flex-shrink-0">
      <Icon size={16} className="text-primary" />
    </div>
    <div>
      <h4 className="font-heading font-semibold text-foreground text-sm">{title}</h4>
      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
    </div>
  </div>
);

export const ImpactSection = () => {
  const impacts = [
    { icon: Zap, title: "Faster onboarding", desc: "Users understand without tutorials." },
    { icon: MessageSquare, title: "Fewer support questions", desc: "Clear interfaces reduce confusion." },
    { icon: Heart, title: "More confident users", desc: "Trust comes from understanding." },
    { icon: Users, title: "Clearer internal discussions", desc: "Teams align on priorities." }
  ];

  return (
    <section id="impact" className="zone-resolution section-tight border-t border-border">
      <div className="container-narrow">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-5">
          <p className="text-[11px] font-medium text-muted-foreground tracking-wide uppercase mb-1.5">Outcomes</p>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-2">
            Business Impact
          </h2>
          <p className="text-sm text-muted-foreground max-w-md">
            Clarity is not aesthetics. It directly affects operations.
          </p>
        </motion.div>

        {/* Wrapped in card to anchor visually */}
        <motion.div 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }} 
          variants={stagger}
          className="impact-wrapper"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {impacts.map((item, i) => (
              <motion.div key={i} variants={fadeUp}>
                <ImpactItem {...item} />
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }} 
          variants={fadeUp}
          className="mt-5 p-4 bg-secondary/30 rounded border border-border"
        >
          <p className="text-sm text-foreground text-center leading-relaxed">
            Design clarity translates to operational clarity. When your product is easy to use, everything downstream becomes simpler.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ImpactSection;
